import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "configureStore";
import {
  fetchMergeHistoryDetail,
  fetchMergeHistoryList,
} from "services/user-select.service";
import type {
  MergeHistoryBatchInfo,
  MergeHistoryDetailError,
  MergeHistoryListRow,
  MergeHistoryStepRow,
  PaginationShape,
} from "types/MergeHistory";

interface MergeHistoryState {
  list: {
    isLoading: boolean;
    isError: boolean;
    rows: MergeHistoryListRow[];
    pagination: PaginationShape | null;
    page: number;
    pageSize: number;
  };
  detail: {
    isLoading: boolean;
    errorCode: MergeHistoryDetailError;
    batch: MergeHistoryBatchInfo | null;
    steps: MergeHistoryStepRow[];
    pagination: PaginationShape | null;
    page: number;
    pageSize: number;
  };
}

const initialState: MergeHistoryState = {
  list: {
    isLoading: false,
    isError: false,
    rows: [],
    pagination: null,
    page: 1,
    pageSize: 25,
  },
  detail: {
    isLoading: false,
    errorCode: null,
    batch: null,
    steps: [],
    pagination: null,
    page: 1,
    pageSize: 25,
  },
};

const mergeHistorySlice = createSlice({
  name: "mergeHistoryContainer",
  initialState,
  reducers: {
    setListLoading(state, action: PayloadAction<boolean>) {
      state.list.isLoading = action.payload;
    },
    setListError(state, action: PayloadAction<boolean>) {
      state.list.isError = action.payload;
    },
    setListData(
      state,
      action: PayloadAction<{
        rows: MergeHistoryListRow[];
        pagination: PaginationShape | null;
      }>
    ) {
      state.list.rows = action.payload.rows;
      state.list.pagination = action.payload.pagination;
    },
    setListPage(state, action: PayloadAction<number>) {
      state.list.page = action.payload;
    },
    setListPageSize(state, action: PayloadAction<number>) {
      state.list.pageSize = action.payload;
      state.list.page = 1;
    },
    resetList(state) {
      state.list = initialState.list;
    },
    setDetailLoading(state, action: PayloadAction<boolean>) {
      state.detail.isLoading = action.payload;
    },
    setDetailData(
      state,
      action: PayloadAction<{
        batch: MergeHistoryBatchInfo | null;
        steps: MergeHistoryStepRow[];
        pagination: PaginationShape | null;
      }>
    ) {
      state.detail.batch = action.payload.batch;
      state.detail.steps = action.payload.steps;
      state.detail.pagination = action.payload.pagination;
    },
    setDetailError(state, action: PayloadAction<MergeHistoryDetailError>) {
      state.detail.errorCode = action.payload;
    },
    setDetailPage(state, action: PayloadAction<number>) {
      state.detail.page = action.payload;
    },
    setDetailPageSize(state, action: PayloadAction<number>) {
      state.detail.pageSize = action.payload;
      state.detail.page = 1;
    },
    resetDetail(state) {
      state.detail = initialState.detail;
    },
  },
});

const { actions, reducer } = mergeHistorySlice;

export const {
  setListLoading,
  setListError,
  setListData,
  setListPage,
  setListPageSize,
  resetList,
  setDetailLoading,
  setDetailData,
  setDetailError,
  setDetailPage,
  setDetailPageSize,
  resetDetail,
} = actions;

export default reducer;

export const fetchMergeHistoryListThunk =
  (companyId: string) => async (dispatch: AppDispatch, getState: () => any) => {
    dispatch(setListLoading(true));
    dispatch(setListError(false));
    try {
      const state = getState();
      const page = state.mergeHistoryContainerReducer?.list?.page || 1;
      const pageSize = state.mergeHistoryContainerReducer?.list?.pageSize || 25;
      const data = await fetchMergeHistoryList(companyId, { page, pageSize });
      dispatch(
        setListData({
          rows: Array.isArray(data?.rows) ? data.rows : [],
          pagination:
            data?.pagination && typeof data.pagination.total === "number"
              ? data.pagination
              : null,
        })
      );
    } catch {
      dispatch(setListError(true));
      dispatch(setListData({ rows: [], pagination: null }));
    } finally {
      dispatch(setListLoading(false));
    }
  };

export const fetchMergeHistoryDetailThunk =
  (companyId: string, mergeBatchId: string) =>
  async (dispatch: AppDispatch, getState: () => any) => {
    dispatch(setDetailLoading(true));
    dispatch(setDetailError(null));
    try {
      const state = getState();
      const page = state.mergeHistoryContainerReducer?.detail?.page || 1;
      const pageSize =
        state.mergeHistoryContainerReducer?.detail?.pageSize || 25;
      const data = await fetchMergeHistoryDetail(companyId, mergeBatchId, {
        page,
        pageSize,
      });
      dispatch(
        setDetailData({
          batch: data?.batch || null,
          steps: Array.isArray(data?.steps) ? data.steps : [],
          pagination:
            data?.pagination && typeof data.pagination.total === "number"
              ? data.pagination
              : null,
        })
      );
    } catch (err: any) {
      const status = err?.response?.status;
      dispatch(setDetailData({ batch: null, steps: [], pagination: null }));
      dispatch(setDetailError(status === 404 ? "not_found" : "load_failed"));
    } finally {
      dispatch(setDetailLoading(false));
    }
  };
